import { useState, useEffect, useCallback } from 'react'
import {
  Table, Group, TextInput, Title, Pagination, Badge, ActionIcon, Modal, Button,
  Stack, Card, Text, Switch, Checkbox, SimpleGrid, PasswordInput, Avatar,
} from '@mantine/core'
import { IconSearch, IconEdit, IconTrash, IconPlus } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'
import { useAuth } from '@/context/AuthContext'
import { ADMIN_PERMISSIONS } from '@/constants/permissions'

interface SystemUser {
  _id: string
  fullName: string
  email: string
  phone?: string
  isSuperAdmin: boolean
  adminPermissions: string[]
  createdAt: string
}

interface FormData {
  fullName: string
  email: string
  phone: string
  password: string
  isSuperAdmin: boolean
  adminPermissions: string[]
}

const emptyForm: FormData = {
  fullName: '',
  email: '',
  phone: '',
  password: '',
  isSuperAdmin: false,
  adminPermissions: [],
}

export default function SystemUserList() {
  const { user } = useAuth()
  const isSuperAdmin = user?.isSuperAdmin ?? false

  const [users, setUsers] = useState<SystemUser[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [search, setSearch] = useState('')
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormData>(emptyForm)
  const [saving, setSaving] = useState(false)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    if (search) params.search = search
    api.admin.getSystemUsers(params).then((res) => {
      setUsers(res.data as unknown as SystemUser[])
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page, search])

  useEffect(() => { load() }, [load])

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  const openEdit = (u: SystemUser) => {
    setEditingId(u._id)
    setForm({
      fullName: u.fullName,
      email: u.email,
      phone: u.phone || '',
      password: '',
      isSuperAdmin: u.isSuperAdmin,
      adminPermissions: u.adminPermissions || [],
    })
    setModalOpen(true)
  }

  const handleSave = async () => {
    if (!form.fullName || !form.email) {
      notifications.show({ title: 'Алдаа', message: 'Нэр, имэйл заавал шаардлагатай', color: 'red' })
      return
    }
    if (!editingId && !form.password) {
      notifications.show({ title: 'Алдаа', message: 'Нууц үг заавал шаардлагатай', color: 'red' })
      return
    }

    setSaving(true)
    try {
      const data: Record<string, unknown> = {
        fullName: form.fullName,
        phone: form.phone,
        isSuperAdmin: form.isSuperAdmin,
        adminPermissions: form.isSuperAdmin ? [] : form.adminPermissions,
      }
      if (form.password) data.password = form.password

      if (editingId) {
        await api.admin.updateSystemUser(editingId, data)
        notifications.show({ title: 'Амжилттай', message: 'Хэрэглэгч шинэчлэгдлээ', color: 'green' })
      } else {
        await api.admin.createSystemUser({ ...data, email: form.email })
        notifications.show({ title: 'Амжилттай', message: 'Хэрэглэгч үүсгэгдлээ', color: 'green' })
      }
      setModalOpen(false)
      load()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Алдаа гарлаа'
      notifications.show({ title: 'Алдаа', message, color: 'red' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deleteSystemUser(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Хэрэглэгч устгагдлаа', color: 'green' })
      load()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Устгахад алдаа гарлаа'
      notifications.show({ title: 'Алдаа', message, color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  const togglePermission = (key: string) => {
    setForm((prev) => ({
      ...prev,
      adminPermissions: prev.adminPermissions.includes(key)
        ? prev.adminPermissions.filter((p) => p !== key)
        : [...prev.adminPermissions, key],
    }))
  }

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={2}>Системийн хэрэглэгчид</Title>
        {isSuperAdmin && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Нэмэх
          </Button>
        )}
      </Group>

      <Group mb="md" wrap="wrap">
        <TextInput
          placeholder="Хайх..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPagination((p) => ({ ...p, page: 1 })) }}
          style={{ flex: 1, minWidth: 200 }}
        />
      </Group>

      <Card shadow="xs" padding={0} radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700} w={50}>#</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Хэрэглэгч</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Төрөл</Table.Th>
                <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Эрхүүд</Table.Th>
                {isSuperAdmin && (
                  <Table.Th tt="uppercase" fz="xs" c="dimmed" fw={700}>Үйлдэл</Table.Th>
                )}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {users.map((u, i) => (
                <Table.Tr key={u._id}>
                  <Table.Td c="dimmed" fz="sm">{(pagination.page - 1) * 20 + i + 1}</Table.Td>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={36} radius="xl">{formatName(u.fullName)?.charAt(0)}</Avatar>
                      <div>
                        <Text size="sm" fw={500}>{formatName(u.fullName)}</Text>
                        <Text size="xs" c="dimmed">{u.email}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={u.isSuperAdmin ? 'red' : 'blue'}>
                      {u.isSuperAdmin ? 'Super Admin' : 'Admin'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {u.isSuperAdmin ? (
                      <Badge variant="light" color="red">Бүх эрх</Badge>
                    ) : (
                      <Group gap={4}>
                        {(u.adminPermissions || []).map((p) => {
                          const perm = ADMIN_PERMISSIONS.find((ap) => ap.key === p)
                          return (
                            <Badge key={p} variant="light" size="xs">
                              {perm?.label || p}
                            </Badge>
                          )
                        })}
                        {(!u.adminPermissions || u.adminPermissions.length === 0) && (
                          <Text size="xs" c="dimmed">Эрх олгоогүй</Text>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  {isSuperAdmin && (
                    <Table.Td>
                      <Group gap={6}>
                        <ActionIcon variant="light" color="blue" radius="md" onClick={() => openEdit(u)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" radius="md" onClick={() => setDeleteId(u._id)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
              {users.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={isSuperAdmin ? 5 : 4}>
                    <Text ta="center" c="dimmed" py="xl">Админ хэрэглэгч олдсонгүй</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>

        {pagination.pages > 1 && (
          <Group justify="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Card>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />

      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? 'Хэрэглэгч засах' : 'Шинэ админ хэрэглэгч'}
        centered
        size="lg"
      >
        <Stack>
          <TextInput
            label="Нэр"
            required
            value={form.fullName}
            onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.currentTarget.value }))}
          />
          <TextInput
            label="Имэйл"
            required
            disabled={!!editingId}
            value={form.email}
            onChange={(e) => setForm((prev) => ({ ...prev, email: e.currentTarget.value }))}
          />
          <TextInput
            label="Утас"
            value={form.phone}
            onChange={(e) => setForm((prev) => ({ ...prev, phone: e.currentTarget.value }))}
          />
          <PasswordInput
            label={editingId ? 'Шинэ нууц үг (хоосон бол өөрчлөхгүй)' : 'Нууц үг'}
            required={!editingId}
            value={form.password}
            onChange={(e) => setForm((prev) => ({ ...prev, password: e.currentTarget.value }))}
          />
          <Switch
            label="Super Admin (бүх эрхтэй)"
            checked={form.isSuperAdmin}
            onChange={(e) => setForm((prev) => ({ ...prev, isSuperAdmin: e.currentTarget.checked }))}
          />

          {!form.isSuperAdmin && (
            <div>
              <Text size="sm" fw={500} mb="xs">Хандах эрхүүд</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {ADMIN_PERMISSIONS.map((perm) => (
                  <Checkbox
                    key={perm.key}
                    label={perm.label}
                    checked={form.adminPermissions.includes(perm.key)}
                    onChange={() => togglePermission(perm.key)}
                  />
                ))}
              </SimpleGrid>
            </div>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Болих</Button>
            <Button onClick={handleSave} loading={saving}>
              {editingId ? 'Хадгалах' : 'Үүсгэх'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  )
}
